import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  AlertTriangle,
  Calendar,
  User,
  MessageSquare,
  Filter,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { EmployeeDocument, EmployeeDocumentType, EmployeeDocumentStatus } from '@/types/legacy';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const documentTypeLabels: Record<EmployeeDocumentType, string> = {
  sick_leave: 'Baja Médica',
  permission: 'Permiso',
  justification: 'Justificación',
  vacation_request: 'Solicitud Vacaciones',
  complaint: 'Queja / Sugerencia',
  other: 'Otro',
};

const documentTypeIcons: Record<EmployeeDocumentType, string> = {
  sick_leave: '🏥',
  permission: '📋',
  justification: '📄',
  vacation_request: '🏖️',
  complaint: '💬',
  other: '📎',
};

const statusConfig: Record<EmployeeDocumentStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  pending: { label: 'Pendiente', variant: 'secondary', icon: <Clock className="w-3 h-3" /> },
  reviewed: { label: 'Revisado', variant: 'outline', icon: <Eye className="w-3 h-3" /> },
  approved: { label: 'Aprobado', variant: 'default', icon: <CheckCircle className="w-3 h-3" /> },
  rejected: { label: 'Rechazado', variant: 'destructive', icon: <XCircle className="w-3 h-3" /> },
};

export function AdminDocumentsPanel() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<EmployeeDocumentStatus | 'all'>('all');
  const [selectedDoc, setSelectedDoc] = useState<EmployeeDocument | null>(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);

  const fetchDocuments = async () => {
    try {
      let query = api
        .from('employee_documents')
        .select('*')
        .eq('is_active', true)
        .order('is_urgent', { ascending: false })
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setDocuments(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los documentos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [filter]);

  const handleStatusChange = async (docId: string, newStatus: EmployeeDocumentStatus, response?: string) => {
    if (!user) return;

    setResponding(true);
    try {
      const updateData: any = {
        status: newStatus,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        is_read: true,
      };

      if (response) {
        updateData.admin_response = response;
      }

      const { error } = await api
        .from('employee_documents')
        .update(updateData)
        .eq('id', docId);

      if (error) throw error;

      toast({
        title: 'Actualizado',
        description: `Documento marcado como ${statusConfig[newStatus].label.toLowerCase()}`,
      });

      setSelectedDoc(null);
      setResponseText('');
      fetchDocuments();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el documento',
        variant: 'destructive',
      });
    } finally {
      setResponding(false);
    }
  };

  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const urgentCount = documents.filter(d => d.is_urgent && d.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Documentos de Empleados
          </h3>
          <p className="text-sm text-muted-foreground">
            Bajas, permisos y solicitudes recibidas
          </p>
        </div>

        <Select value={filter} onValueChange={(v) => setFilter(v as any)}>
          <SelectTrigger className="w-40">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="reviewed">Revisados</SelectItem>
            <SelectItem value="approved">Aprobados</SelectItem>
            <SelectItem value="rejected">Rechazados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold">{documents.length}</p>
          <p className="text-xs text-muted-foreground">Total</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-yellow-500">{pendingCount}</p>
          <p className="text-xs text-muted-foreground">Pendientes</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-red-500">{urgentCount}</p>
          <p className="text-xs text-muted-foreground">Urgentes</p>
        </Card>
        <Card className="p-3 text-center">
          <p className="text-2xl font-bold text-green-500">
            {documents.filter(d => d.status === 'approved').length}
          </p>
          <p className="text-xs text-muted-foreground">Aprobados</p>
        </Card>
      </div>

      {/* Alerta de urgentes */}
      {urgentCount > 0 && (
        <Card className="p-4 bg-red-500/10 border-red-500/30">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-600">
              {urgentCount} documento(s) urgente(s) pendiente(s) de revisión
            </span>
          </div>
        </Card>
      )}

      {/* Lista */}
      {documents.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No hay documentos</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => {
            const status = statusConfig[doc.status];

            return (
              <Card
                key={doc.id}
                className={`p-4 hover:shadow-md transition-shadow cursor-pointer ${
                  doc.is_urgent && doc.status === 'pending' ? 'border-red-500/50 bg-red-500/5' : ''
                }`}
                onClick={() => setSelectedDoc(doc)}
              >
                <div className="flex items-start gap-4">
                  <span className="text-2xl">{documentTypeIcons[doc.document_type]}</span>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          {doc.title}
                          {doc.is_urgent && (
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          )}
                        </h4>
                        <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                          <User className="w-3 h-3" />
                          {doc.employee_name || 'Empleado'}
                          <span className="mx-1">·</span>
                          {documentTypeLabels[doc.document_type]}
                        </p>
                      </div>
                      <Badge variant={status.variant} className="flex items-center gap-1">
                        {status.icon}
                        {status.label}
                      </Badge>
                    </div>

                    {doc.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {doc.description}
                      </p>
                    )}

                    {(doc.start_date || doc.end_date) && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {doc.start_date && format(new Date(doc.start_date), 'dd/MM/yyyy')}
                        {doc.start_date && doc.end_date && ' - '}
                        {doc.end_date && format(new Date(doc.end_date), 'dd/MM/yyyy')}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground mt-2">
                      Recibido {formatDistanceToNow(new Date(doc.created_at), {
                        addSuffix: true,
                        locale: es
                      })}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog de detalle y respuesta */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-lg">
          {selectedDoc && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="text-xl">{documentTypeIcons[selectedDoc.document_type]}</span>
                  {selectedDoc.title}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{selectedDoc.employee_name}</span>
                  <Badge variant={statusConfig[selectedDoc.status].variant}>
                    {statusConfig[selectedDoc.status].label}
                  </Badge>
                  {selectedDoc.is_urgent && (
                    <Badge variant="destructive">Urgente</Badge>
                  )}
                </div>

                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm">{selectedDoc.description || 'Sin descripción'}</p>
                </div>

                {(selectedDoc.start_date || selectedDoc.end_date) && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {selectedDoc.start_date && format(new Date(selectedDoc.start_date), 'dd/MM/yyyy')}
                      {selectedDoc.start_date && selectedDoc.end_date && ' - '}
                      {selectedDoc.end_date && format(new Date(selectedDoc.end_date), 'dd/MM/yyyy')}
                    </span>
                  </div>
                )}

                {selectedDoc.file_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedDoc.file_name}</span>
                  </div>
                )}

                {selectedDoc.admin_response && (
                  <div className="p-3 rounded-lg bg-primary/10 border-l-4 border-primary">
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Tu respuesta anterior:
                    </p>
                    <p className="text-sm">{selectedDoc.admin_response}</p>
                  </div>
                )}

                {selectedDoc.status === 'pending' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Respuesta (opcional)
                    </label>
                    <Textarea
                      placeholder="Escribe una respuesta para el empleado..."
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      rows={3}
                    />
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                {selectedDoc.status === 'pending' && (
                  <>
                    <Button
                      variant="destructive"
                      onClick={() => handleStatusChange(selectedDoc.id, 'rejected', responseText)}
                      disabled={responding}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Rechazar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleStatusChange(selectedDoc.id, 'reviewed', responseText)}
                      disabled={responding}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Marcar Revisado
                    </Button>
                    <Button
                      onClick={() => handleStatusChange(selectedDoc.id, 'approved', responseText)}
                      disabled={responding}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Aprobar
                    </Button>
                  </>
                )}
                {selectedDoc.status !== 'pending' && (
                  <Button variant="outline" onClick={() => setSelectedDoc(null)}>
                    Cerrar
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
